import { Text } from '@sitecore-jss/sitecore-jss-react';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { errorMessages } from '../../../utils/errors/constants';
import useErrorState from '../../../utils/errors/useErrorState';
import { resolve } from '../../../utils/object/pathResolver';

const InputQuestion = ({
  rendering,
  fields,
  displayErrors,
  defaultValue,
  onValidEntry,
  onError,
  onBlur,
  currentError,
}) => {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState(defaultValue);
  const [error, setError] = useErrorState(rendering.uid, fields.name?.value, fields, onError);
  const errorClass = error && displayErrors ? ' vds-form__input--invalid' : '';

  const validate = value => {
    const validation = fields.validation?.fields;
    if (!validation) {
      return true;
    }
    if (validation.required?.value && (!value || value === '')) {
      setError(errorMessages.EMPTY);
      return false;
    }
    if (validation.minLength?.value && value.length < validation.minLength.value) {
      setError(errorMessages.TOO_SHORT);
      return false;
    }
    if (validation.maxLength?.value && value.length > validation.maxLength.value) {
      setError(errorMessages.TOO_LONG);
      return false;
    }
    if (validation.regex?.value) {
      const regex = new RegExp(validation.regex.value, 'g');
      if (!regex.test(value)) {
        setError(errorMessages.INVALID);
        return false;
      }
    }
    setError(null);
    return true;
  };

  const normaliseValue = value => {
    const validation = fields.validation?.fields;
    if (!validation) {
      return value;
    }
    let normalisedValue = value || '';
    if (fields.type?.value?.toLowerCase() === 'sortcode') {
      normalisedValue = normalisedValue.replace(/-/g, '');
    }
    if (validation.lengthLimited?.value && validation.maxLength?.value) {
      normalisedValue = normalisedValue.substring(0, validation.maxLength.value);
    }
    return normalisedValue;
  };

  const handleUpdate = e => {
    const value = normaliseValue(e.target.value);
    setInputValue(value);
    if (validate(value)) {
      if (typeof onValidEntry === 'function') {
        onValidEntry(value);
      }
      let dispatchData = {};
      if (fields.reduxDispatchType?.value) {
        dispatchData = {
          type: fields.reduxDispatchType?.value,
          payload: value,
        };
        dispatch(dispatchData);
      }
      const index = fields.reduxDispatchType?.payload?.index;
      dispatchData = {
        type: fields.reduxDispatchType?.value,
        payload: index >= 0 ? { value, index } : value,
      };
      dispatch(dispatchData);
    }
  };

  const handleFormat = e => {
    let value = normaliseValue(e.target.value);
    value = value.trim();
    setInputValue(value);
    if (validate(value)) {
      const dispatchData = {
        type: fields.reduxDispatchType?.value,
        payload: value,
      };
      dispatch(dispatchData);
    }
    if (fields.validation?.fields?.format?.value) {
      const formattedValue = value.replace(/\s+/g, '').replace(/(\(\+44\)|\[\+44\]|\+44)/g, '0');
      setInputValue(formattedValue);
      if (validate(formattedValue)) {
        const dispatchData = {
          type: fields.reduxDispatchType?.value,
          payload: formattedValue,
        };
        dispatch(dispatchData);
      }
    }
  };

  const handleBlur = customOnBlur => e => {
    if (customOnBlur) {
      customOnBlur(e);
    }
    handleFormat(e);
  };

  useEffect(() => {
    if (fields.required?.value && !inputValue) {
      setError(errorMessages.EMPTY);
    }
    return () => {
      setError(null);
    };
  }, []);

  useEffect(() => {
    if (defaultValue && !inputValue) {
      setInputValue(defaultValue);
      if (validate(defaultValue)) {
        setError(null);
      }
    }
  }, [defaultValue]);

  const getValue = () => {
    if (inputValue && fields.type?.value?.toLowerCase() === 'sortcode') {
      return inputValue.replace(/[0-9]{2}/g, '$&-').replace(/-$/, '');
    }
    return inputValue;
  };

  return (
    <div className="vds-form__group vds-form__group--limited-width">
      <Text
        tag="label"
        htmlFor={fields.name?.value}
        className="vds-form__label show"
        field={fields.label}
      />
      <input
        id={fields.name?.value}
        name={fields.name?.value}
        type={fields.type?.value?.toLowerCase() === 'sortcode' ? 'tel' : fields.type?.value}
        placeholder={fields.placeholder?.value}
        className={`vds-form__input${errorClass}`}
        data-testid={fields.testid?.value}
        value={getValue()}
        onChange={handleUpdate}
        onBlur={onBlur ? handleBlur(onBlur) : handleFormat}
        required={fields.required?.value}
      />
      {displayErrors && fields.inlineError?.value && (error || currentError) && (
        <div className="vds-form__group">
          <div className="vds-form__validation-message vds-form__validation-message--invalid">
            <span
              className="vds-form__validation-text"
              data-errormessageorigin={error?.name || currentError?.name}
              data-errormessagetype={error?.type || currentError?.type}
              data-testid="error-message"
            >
              {error?.message || currentError?.message}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state, props) => {
  const reduxValuePath = props.fields.reduxValuePath?.value;
  let defaultValue;
  if (reduxValuePath) {
    defaultValue = resolve(reduxValuePath, state);
  }
  return {
    ...props,
    defaultValue,
    displayErrors: props.displayErrors || state.appState.displayErrors,
  };
};
export default connect(mapStateToProps)(InputQuestion);
